import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useToast } from '../../../src/contexts/ToastContext';
import LoadingSpinner from '../../../src/components/LoadingSpinner';
import { programaAtivo, salvarPrograma } from '../../../src/services/indicacoes';
import { parseValorReais } from '../../../src/lib/indicacoes/formulario';
import { ROTULO_TIPO_RECOMPENSA, formatarDataHora } from '../../../src/lib/indicacoes/rotulos';
import type { IndicacaoPrograma, RecompensaTipo } from '../../../types';
import { CLASSE_BOTAO_PRIMARIO, CLASSE_CARTAO, CLASSE_INPUT, CLASSE_ROTULO } from './estilos';

const paraTexto = (v: number | null) => (v != null ? String(v).replace('.', ',') : '');

const ProgramaTab: React.FC<{ podeEditar: boolean }> = ({ podeEditar }) => {
  const { addToast } = useToast();
  const [prog, setProg] = useState<IndicacaoPrograma | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [tipoCliente, setTipoCliente] = useState<RecompensaTipo | ''>('');
  const [valorCliente, setValorCliente] = useState('');
  const [tipoNaoCliente, setTipoNaoCliente] = useState<'dinheiro' | 'brinde'>('dinheiro');
  const [valorNaoCliente, setValorNaoCliente] = useState('');
  const [brinde, setBrinde] = useState('');

  const preencher = (p: IndicacaoPrograma | null) => {
    setProg(p);
    if (!p) return;
    setTipoCliente(p.recompensa_cliente_tipo ?? '');
    setValorCliente(paraTexto(p.valor_cliente));
    setTipoNaoCliente(p.recompensa_nao_cliente_tipo);
    setValorNaoCliente(paraTexto(p.valor_nao_cliente));
    setBrinde(p.descricao_brinde ?? '');
  };

  useEffect(() => {
    programaAtivo()
      .then(preencher)
      .catch(e => addToast((e as Error).message, 'error'))
      .finally(() => setCarregando(false));
  }, []);

  const salvar = async () => {
    const vc = valorCliente.trim() ? parseValorReais(valorCliente) : null;
    const vn = valorNaoCliente.trim() ? parseValorReais(valorNaoCliente) : null;
    if (valorCliente.trim() && vc === null) {
      addToast('Valor para cliente inválido.', 'warning');
      return;
    }
    if (valorNaoCliente.trim() && vn === null) {
      addToast('Valor para não cliente inválido.', 'warning');
      return;
    }
    setSalvando(true);
    try {
      await salvarPrograma({
        recompensaClienteTipo: tipoCliente || null,
        valorCliente: vc,
        recompensaNaoClienteTipo: tipoNaoCliente,
        valorNaoCliente: vn,
        descricaoBrinde: brinde.trim() || null,
      });
      preencher(await programaAtivo());
      addToast('Programa salvo.', 'success');
    } catch (e) {
      addToast((e as Error).message, 'error');
    } finally {
      setSalvando(false);
    }
  };

  if (carregando) return <LoadingSpinner label="Carregando programa..." />;
  if (!prog) return <p className="text-sm text-gray-500 dark:text-slate-400">Não há programa de indicações ativo.</p>;

  return (
    <div className={`${CLASSE_CARTAO} p-5 space-y-5 max-w-3xl`}>
      <div>
        <h2 className="text-base font-bold text-gray-900 dark:text-white">{prog.nome}</h2>
        <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
          Vale para as recompensas geradas a partir de agora. As que já existem mantêm o valor do momento da aprovação;
          ajuste uma a uma na aba Recompensas. Última alteração: {formatarDataHora(prog.atualizado_em)}.
        </p>
      </div>
      <fieldset disabled={!podeEditar} className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">Quem é cliente</p>
          <div>
            <label className={CLASSE_ROTULO}>Tipo</label>
            <select className={CLASSE_INPUT} value={tipoCliente} onChange={e => setTipoCliente(e.target.value as RecompensaTipo | '')}>
              <option value="">A decidir</option>
              {(Object.keys(ROTULO_TIPO_RECOMPENSA) as RecompensaTipo[]).map(t => (
                <option key={t} value={t}>{ROTULO_TIPO_RECOMPENSA[t]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={CLASSE_ROTULO}>Valor (R$)</label>
            <input className={CLASSE_INPUT} value={valorCliente} onChange={e => setValorCliente(e.target.value)} placeholder="A definir" />
          </div>
        </div>
        <div className="space-y-2">
          <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">Quem não é cliente</p>
          <div>
            <label className={CLASSE_ROTULO}>Tipo</label>
            <select
              className={CLASSE_INPUT}
              value={tipoNaoCliente}
              onChange={e => setTipoNaoCliente(e.target.value as 'dinheiro' | 'brinde')}
            >
              <option value="dinheiro">{ROTULO_TIPO_RECOMPENSA.dinheiro}</option>
              <option value="brinde">{ROTULO_TIPO_RECOMPENSA.brinde}</option>
            </select>
          </div>
          <div>
            <label className={CLASSE_ROTULO}>Valor (R$)</label>
            <input className={CLASSE_INPUT} value={valorNaoCliente} onChange={e => setValorNaoCliente(e.target.value)} placeholder="A definir" />
          </div>
        </div>
        <div className="sm:col-span-2">
          <label className={CLASSE_ROTULO}>Descrição do brinde</label>
          <input className={CLASSE_INPUT} value={brinde} onChange={e => setBrinde(e.target.value)} placeholder="Opcional" />
        </div>
      </fieldset>
      {podeEditar ? (
        <button onClick={salvar} disabled={salvando} className={CLASSE_BOTAO_PRIMARIO}>
          {salvando && <Loader2 size={15} className="animate-spin" />} Salvar
        </button>
      ) : (
        <p className="text-xs text-gray-500 dark:text-slate-400">Só administradores e gerentes alteram o programa.</p>
      )}
    </div>
  );
};

export default ProgramaTab;
